import { isAxiosError } from 'axios';
import { Credentials, Session, Role, Organization } from '@/models/user';
import { API, hasBackend } from './api';
import { mockUser } from './mock-data';
import { LLMService } from './llm.service';
import { LibraryService } from './library.service';
import { getOrganization } from '@/data/organizations';
import { AdminService } from './admin.service';
import { getCurrentOrg } from './org.service';
import { VISITOR } from '@/models/session';

const ORG_CONFIG_STORAGE_KEY = 'workspace-org-config';

interface StoredOrgConfig {
  organization_id: string;
  name: string;
  logo_url?: string;
  auth_method?: 'password' | 'sso';
}

/**
 * Gets the stored organization config from localStorage.
 */
const getStoredOrgConfig = (): StoredOrgConfig | null => {
  try {
    const stored = localStorage.getItem(ORG_CONFIG_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

/**
 * Builds an Organization object by merging stored config with fallback data.
 *
 * Note: The stored config uses the organization subdomain/slug as organization_id
 * (from /auth/org-config), while orgId parameter is the database ID (from /auth-new/me).
 * These don't match, so we use stored config when available without ID comparison,
 * since it was set during the same SSO flow for the current subdomain.
 */
const buildOrganization = (orgId: string): Organization => {
  const storedConfig = getStoredOrgConfig();
  const fallbackOrg = getOrganization(orgId);

  // If we have stored config, use its name, logo, and auth_method for branding.
  // We don't compare organization_id because the stored config uses subdomain/slug
  // while orgId is the database ID - they use different identifier systems.
  // Use nullish coalescing to avoid overwriting fallback values with undefined.
  if (storedConfig && storedConfig.name) {
    return {
      ...fallbackOrg,
      id: orgId,
      name: storedConfig.name ?? fallbackOrg.name,
      logo: storedConfig.logo_url ?? fallbackOrg.logo,
      auth_method: storedConfig.auth_method ?? fallbackOrg.auth_method,
    };
  }

  return fallbackOrg;
};

/**
 * Response from the organization configuration endpoint.
 */
export interface OrgConfigResponse {
  organization_id: string;
  name: string;
  auth_method: 'password' | 'sso';
  zitadel_auth_url?: string;
  logo_url?: string;
  idp_name?: string;
}

/**
 * Response from the SSO callback endpoint.
 */
export interface SSOCallbackResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

/**
 * Response from the SSO logout endpoint.
 * Contains the Zitadel end_session URL for completing the logout.
 */
export interface SSOLogoutResponse {
  end_session_url: string;
  tokens_revoked: boolean;
}

export const AuthService = {
  login: async ({ email, password }: Credentials): Promise<Session> => {
    if (!hasBackend) {
      if (email === 'j.doe@ipcinfo.org') {
        localStorage.setItem('st-access-token', 'tmp');
        localStorage.setItem('st-refresh-token', 'tmp');

        return mockUser;
      } else {
        throw new Error('Authentication failed');
      }
    }

    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    try {
      const response = (await API.post('/auth-new/token', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })) as {
        data: {
          access_token: string;
          refresh_token: string;
        };
      };

      // Clear any stored org config from previous SSO logins to prevent
      // SSO auth_method from leaking into password-based sessions
      localStorage.removeItem(ORG_CONFIG_STORAGE_KEY);

      const user = await initSession(
        response.data.access_token,
        response.data.refresh_token
      );

      return user;
    } catch (error) {
      console.error('Login failed:', error);
      if (isAxiosError(error) && error.response?.status === 401) {
        throw new Error(error.response.data?.detail || 'Invalid credentials');
      }
      throw new Error('An error occurred. Please try again later');
    }
  },
  me: async (): Promise<Session> => {
    if (!hasBackend) {
      return mockUser;
    }

    const response = (await API.get('/auth-new/me')) as {
      data: {
        user_id: string;
        email: string;
        name: string;
        roles: Array<Role>;
        organization_id: string;
      };
    };
    let orgID: string | undefined = response.data.organization_id;

    const session: Session = {
      id: response.data.user_id,
      email: response.data.email,
      name: response.data.name,
      roles: response.data.roles,
      fileCount: 0,
      threads: [],
      organizations: [buildOrganization(orgID)],
    };

    if (session.roles.includes(Role.SUPER_ORG_ADMIN)) {
      const organizations = await AdminService.getOrganizations();

      session.organizations = organizations.map((org) => {
        const mockedOrg = getOrganization(org.id);

        return {
          ...org,
          logo: mockedOrg.logo,
          suggestions: mockedOrg.suggestions,
        };
      });

      const storedOrgID = getCurrentOrg();

      if (storedOrgID !== null) {
        orgID = storedOrgID;
      }
    }

    if (!session.roles.includes(Role.SUPER_DATA_ADMIN)) {
      orgID = undefined;
    }

    // Gracefully handle missing endpoints (e.g., during SSO testing)
    let fileCount = 0;
    let chats: Awaited<ReturnType<typeof LLMService.getThreads>> = [];
    try {
      fileCount = await LibraryService.getTotalFilesCount(orgID);
    } catch {
      console.warn('Failed to fetch file count, defaulting to 0');
    }
    try {
      chats = await LLMService.getThreads();
    } catch {
      console.warn('Failed to fetch threads, defaulting to empty array');
    }

    return {
      ...session,
      fileCount: fileCount,
      threads: chats,
    };
  },

  /**
   * Fetches user info for SSO-authenticated users.
   * Calls /auth/me which validates Zitadel tokens via the userinfo endpoint,
   * unlike the regular me() which uses /auth-new/me for backend JWTs.
   */
  ssoMe: async (): Promise<Session> => {
    if (!hasBackend) {
      return mockUser;
    }

    const response = (await API.get('/auth/me')) as {
      data: {
        user_id: string;
        email: string;
        name: string;
        roles: Array<Role>;
        organization_id: string;
      };
    };
    let orgID: string | undefined = response.data.organization_id;

    const session: Session = {
      id: response.data.user_id,
      email: response.data.email,
      name: response.data.name,
      roles: response.data.roles,
      fileCount: 0,
      threads: [],
      organizations: [buildOrganization(orgID)],
    };

    if (session.roles.includes(Role.SUPER_ORG_ADMIN)) {
      const organizations = await AdminService.getOrganizations();

      session.organizations = organizations.map((org) => {
        const mockedOrg = getOrganization(org.id);

        return {
          ...org,
          logo: mockedOrg.logo,
          suggestions: mockedOrg.suggestions,
        };
      });

      const storedOrgID = getCurrentOrg();

      if (storedOrgID !== null) {
        orgID = storedOrgID;
      }
    }

    if (!session.roles.includes(Role.SUPER_DATA_ADMIN)) {
      orgID = undefined;
    }

    // Gracefully handle missing endpoints (e.g., during SSO testing)
    let fileCount = 0;
    let chats: Awaited<ReturnType<typeof LLMService.getThreads>> = [];
    try {
      fileCount = await LibraryService.getTotalFilesCount(orgID);
    } catch {
      console.warn('Failed to fetch file count, defaulting to 0');
    }
    try {
      chats = await LLMService.getThreads();
    } catch {
      console.warn('Failed to fetch threads, defaulting to empty array');
    }

    return {
      ...session,
      fileCount: fileCount,
      threads: chats,
    };
  },

  /**
   * Logs out the user and clears local storage.
   * For SSO users, also revokes tokens at Zitadel and returns the
   * end_session URL for completing the logout flow.
   *
   * @returns The end_session URL for SSO users (redirect here to complete logout),
   *          or null for password-auth users
   */
  logout: async (): Promise<string | null> => {
    const authType = localStorage.getItem('st-auth-type');
    const isSsoAuth = authType === 'sso';
    const accessToken = localStorage.getItem('st-access-token');
    const refreshToken = localStorage.getItem('st-refresh-token');

    // Clear local storage first (regardless of backend call success)
    localStorage.removeItem('st-access-token');
    localStorage.removeItem('st-refresh-token');
    localStorage.removeItem('st-auth-type');
    localStorage.removeItem(ORG_CONFIG_STORAGE_KEY);

    // For SSO users, call backend to revoke tokens and get end_session URL
    if (isSsoAuth && accessToken && hasBackend) {
      try {
        // Build the post-logout redirect URL (root URL - must match Zitadel config)
        // Include trailing slash to match typical Zitadel URI format
        const postLogoutRedirectUri = `${window.location.origin}/`;

        const response = await API.post<SSOLogoutResponse>('/auth/logout', {
          access_token: accessToken,
          refresh_token: refreshToken || undefined,
          post_logout_redirect_uri: postLogoutRedirectUri,
        });

        // Return the end_session URL for the caller to redirect to
        return response.data.end_session_url;
      } catch (error) {
        console.error('SSO logout failed:', error);
        // Even if backend call fails, local tokens are cleared
        // Return null to proceed with local-only logout
        return null;
      }
    }

    return null;
  },
  refreshSession: async (): Promise<Session> => {
    try {
      const refreshToken = localStorage.getItem('st-refresh-token');
      const authType = localStorage.getItem('st-auth-type');
      const isSsoAuth = authType === 'sso';

      if (!hasBackend) {
        if (refreshToken && refreshToken.length < 5) {
          localStorage.setItem(
            'st-access-token',
            `${refreshToken}${refreshToken?.length}`
          );
          return mockUser;
        } else {
          throw new Error('Session expired. Please log in again.');
        }
      }

      if (!refreshToken) {
        return VISITOR;
      }

      // Use different endpoint based on auth type:
      // - SSO: /auth/token/refresh (Zitadel tokens)
      // - Password: /auth-new/refresh (backend JWTs)
      const endpoint = isSsoAuth ? '/auth/token/refresh' : '/auth-new/refresh';

      const response = await API.post(endpoint, {
        refresh_token: refreshToken,
      });

      const user = await initSession(
        response.data.access_token,
        response.data.refresh_token,
        isSsoAuth
      );

      return user;
    } catch (error) {
      console.error(error);
      localStorage.removeItem('st-access-token');
      localStorage.removeItem('st-refresh-token');
      localStorage.removeItem('st-auth-type');
      throw new Error('Session expired. Please log in again.');
    }
  },
  isAuthenticated: () => {
    const token = localStorage.getItem('st-access-token');

    if (!hasBackend) {
      if (token && token.length < 8) {
        localStorage.setItem('st-access-token', `${token}${token?.length}`);
      } else {
        localStorage.removeItem('st-access-token');
        localStorage.removeItem('st-refresh-token');
      }
    }

    return !!token;
  },

  /**
   * Fetches organization authentication configuration.
   * Used to determine if an organization uses SSO or password authentication.
   *
   * @param orgIdentifier - The organization subdomain or identifier
   * @returns Organization configuration including auth method and SSO URL if applicable
   */
  getOrgConfig: async (orgIdentifier: string): Promise<OrgConfigResponse> => {
    if (!hasBackend) {
      // Mock response for development without backend
      return {
        organization_id: 'mock-org-id',
        name: 'Mock Organization',
        auth_method: 'password',
      };
    }

    try {
      const response = await API.get<OrgConfigResponse>('/auth/org-config', {
        params: { subdomain: orgIdentifier },
      });

      return response.data;
    } catch (error) {
      console.error('Failed to fetch organization config:', error);
      if (isAxiosError(error) && error.response) {
        if (error.response.status === 404) {
          throw new Error('Organization not found');
        }
        if (error.response.data?.detail) {
          throw new Error(error.response.data.detail);
        }
      }
      throw new Error('Failed to fetch organization configuration');
    }
  },

  /**
   * Handles the SSO callback after user authenticates with the identity provider.
   * Exchanges the authorization code for tokens and initializes the user session.
   *
   * @param code - The authorization code from the identity provider
   * @param state - The state parameter for CSRF protection
   * @returns The authenticated user session
   */
  handleSSOCallback: async (code: string, state: string): Promise<Session> => {
    if (!hasBackend) {
      // Mock response for development without backend
      localStorage.setItem('st-access-token', 'sso-mock-token');
      localStorage.setItem('st-refresh-token', 'sso-mock-refresh');
      return mockUser;
    }

    try {
      // Use native Zitadel tokens directly - backend has dual-token validation
      const response = await API.get<SSOCallbackResponse>(
        '/auth/sso/callback',
        {
          params: { code, state },
        }
      );

      // SSO users get Zitadel tokens - use SSO auth endpoints
      const user = await initSession(
        response.data.access_token,
        response.data.refresh_token,
        true // Zitadel tokens - use SSO auth endpoints (/auth/me, /auth/token/refresh)
      );

      return user;
    } catch (error) {
      console.error('SSO callback failed:', error);

      // Handle specific HTTP status codes
      if (isAxiosError(error) && error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail;

        if (status === 403) {
          // Seat limit reached - throw with status for UI handling
          const err = Object.assign(
            new Error(
              detail ||
                'Organization has reached its seat limit. Please contact your administrator.'
            ),
            { status: 403 }
          );
          throw err;
        } else if (status === 401) {
          throw new Error(detail || 'SSO authentication failed');
        } else if (status === 400) {
          throw new Error(detail || 'Invalid SSO callback parameters');
        } else if (detail) {
          throw new Error(detail);
        }
      }

      throw new Error('SSO authentication failed. Please try again.');
    }
  },
};

const initSession = async (
  accessToken: string,
  refreshToken: string,
  isSsoLogin: boolean = false
): Promise<Session> => {
  if (accessToken && refreshToken) {
    localStorage.setItem('st-access-token', accessToken);
    localStorage.setItem('st-refresh-token', refreshToken);

    // Track auth type for refresh logic - SSO uses Zitadel tokens,
    // password auth uses backend JWTs
    localStorage.setItem('st-auth-type', isSsoLogin ? 'sso' : 'password');

    // Use appropriate me() endpoint based on auth type
    const user = isSsoLogin
      ? await AuthService.ssoMe()
      : await AuthService.me();

    return user;
  } else {
    throw new Error('Authentication failed: contact your administrator');
  }
};
